import { logger } from '@dealerportal/core';
import { ServerRequire } from './ServerRequire';

/**
 * A set of valid CSS property names, taken from the W3 reference
 */
let ValidCSSPropertySet: Set<string> = new Set();

// Only load property set on server, client doesn't need it
try {
  if (IS_SERVER) {
    import('../../w3-valid-css-properties.json').then((properties) => {
      ValidCSSPropertySet = properties.default.reduce(
        (acc: Set<string>, p: { property: string }) => acc.add(p.property),
        new Set() as Set<string>
      );
    });
  }
} catch (e) {
  // ignore
}

/**
 * Runs CleanCSS on the passed in CSS stylesheet to optimize/minify it
 * Only runs on server, it is a no-opt on browser
 * @param css The CSS to optimize
 * @param origin An optional origin for logging where the style came from
 * @returns A minified/optimized version of the passed in CSS
 */
export function OptimizeCSS(css: string, origin?: string): string {
  if (!IS_SERVER) return css;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const CleanCSS = ServerRequire<any>('clean-css');
    const optimizer = new CleanCSS({
      level: 2,
      plugins: [RemoveInvalidPropertiesPlugin(ValidCSSPropertySet)],
    });
    const optimized = optimizer.minify(css);
    return optimized.styles;
  } catch (e) {
    logger.warn(`Error optimizing inline <style> for '${origin}'`);
    logger.warn('Using unoptimized style...');
    logger.withError(e).error('Error details:');
    return css;
  }
}

/**
 * CleanCSS plugin to remove invalid CSS properties
 */
const RemoveInvalidPropertiesPlugin = (ValidCSSPropertySet: Set<string>) => ({
  level1: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    property: function removeInvalidProperties(_rule: any, property: any) {
      const propertyName: string = property.name;
      const isCSSVarOrVendorSpecificPrefix = propertyName.startsWith('-');
      if (
        ValidCSSPropertySet.size > 0 &&
        !ValidCSSPropertySet.has(propertyName) &&
        !isCSSVarOrVendorSpecificPrefix
      ) {
        property.unused = true;
      }
    },
  },
});
